import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Realtime } from './realtime';
import type { Listener, Stream } from './stream';

const Context = createContext<Realtime | undefined>(undefined);

export function RealtimeProvider({ realtime, children }: { realtime: Realtime; children: ReactNode }) {
	return <Context.Provider value={realtime}>{children}</Context.Provider>;
}

export function useRealtime(): Realtime {
	const realtime = useContext(Context);
	if (!realtime) throw new Error('useRealtime needs a RealtimeProvider');
	return realtime;
}


/**
 * Opens the stream of a channel for the life of the component and closes it afterwards. The listener may
 * change between renders; the stream keeps calling the latest one.
 */
export function useStream(channel: string | undefined, listener: Listener): Stream | undefined {
	const realtime = useRealtime();
	const latest = useRef(listener);
	latest.current = listener;
	const [stream, setStream] = useState<Stream>();

	useEffect(() => {
		if (!channel) return;
		const opened = realtime.open(channel, { snapshot: snapshot => latest.current.snapshot(snapshot), event: event => latest.current.event(event) });
		setStream(opened);
		return () => {
			realtime.close(opened);
			setStream(undefined);
		};
	}, [realtime, channel]);

	return stream;
}

